import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const integrations = [
  {
    name: 'Open Journal Systems (OJS)',
    status: 'coming-soon',
    logo: 'OJS',
    bg: '#1a3a5c',
    desc: 'Native plugin integration with OJS to automatically screen manuscript submissions for plagiarism and AI-generated content before peer review.',
    features: ['Automatic manuscript screening on submission', 'Similarity report attached to submission record', 'Editorial dashboard access for editors', 'AI content detection included'],
  },
  {
    name: 'Moodle',
    status: 'planned',
    logo: 'M',
    bg: '#f98012',
    desc: 'Direct integration with Moodle LMS to allow educators to check student assignment submissions for plagiarism without leaving their course environment.',
    features: ['Assignment-level plagiarism checking', 'Instructor report access', 'Student submission history', 'Grade book compatibility'],
  },
  {
    name: 'Canvas',
    status: 'planned',
    logo: 'C',
    bg: '#e66000',
    desc: 'Seamless Canvas integration enabling automatic plagiarism detection for assignments submitted through the Canvas learning management system.',
    features: ['LTI-based integration', 'Automatic submission screening', 'Instructor and student report views', 'SpeedGrader compatibility'],
  },
  {
    name: 'Blackboard',
    status: 'planned',
    logo: 'B',
    bg: '#333333',
    desc: 'Blackboard Building Block integration for institutional plagiarism detection across all Blackboard Learn deployments.',
    features: ['Building Block plugin', 'Assignment submission screening', 'Grade Centre integration', 'Institutional reporting'],
  },
];

const apiFeatures = [
  {
    title: 'REST API',
    desc: 'Submit manuscripts programmatically and retrieve similarity and AI detection reports via our documented REST API. Suitable for any custom integration.',
  },
  {
    title: 'Webhook Callbacks',
    desc: 'Receive real-time notifications when reports are ready. Build automated workflows that act on results without polling.',
  },
  {
    title: 'API Key Management',
    desc: 'Issue and manage API keys per team member or integration. Monitor usage and revoke access from a central dashboard.',
  },
  {
    title: 'OJS Plugin (Coming Soon)',
    desc: 'A dedicated MannaPlag plugin for Open Journal Systems that integrates directly into the OJS submission and review workflow.',
  },
];

function StatusBadge({ status }: { status: string }) {
  if (status === 'coming-soon') return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: '#eff6ff', color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      Coming soon
    </span>
  );
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: '#f3f4f6', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      Planned
    </span>
  );
}

export default function LMSIntegration() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="LMS & Journal Integrations: Moodle, Canvas, OJS"
        description="Seamlessly integrate MannaPlag with Moodle, Canvas, Blackboard, and Open Journal Systems (OJS) for automated academic integrity and manuscript screening."
        canonicalPath="/lms-integration"
        keywords="moodle plagiarism plugin, canvas plagiarism checker, ojs manuscript screening, lti plagiarism tool, lms academic integrity integration"
      />

      {/* Hero */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-[36px] font-black text-gray-900 mb-4 tracking-tight leading-tight">
            LMS and journal system integrations
          </h1>
          <p className="text-[15px] text-gray-500 max-w-2xl mx-auto leading-relaxed">
            MannaPlag is actively building native integrations with leading learning management systems and open journal platforms. Our REST API is available today for custom integrations.
          </p>
          <div className="flex items-center justify-center gap-4 mt-8 flex-wrap">
            <Link to="/api-reference" className="inline-block bg-navy text-white text-[13px] font-bold px-6 py-3 rounded-md hover:opacity-90 transition-opacity">
              View API docs
            </Link>
            <Link to="/contact?inquiry=integration" className="inline-block border border-gray-200 text-gray-700 text-[13px] font-bold px-6 py-3 rounded-md hover:bg-gray-50 transition-colors">
              Request an integration
            </Link>
          </div>
        </div>
      </div>

      {/* Integration cards */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-[28px] font-black text-gray-900 mb-3">Supported platforms</h2>
          <p className="text-[14px] text-gray-500 max-w-xl mx-auto">
            We are actively developing integrations with the most widely used journal and learning management systems in academic publishing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {integrations.map(intg => (
            <div key={intg.name} className="border border-gray-100 rounded-lg p-6 hover:border-gray-200 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: intg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 900, fontSize: 16, flexShrink: 0 }}>
                    {intg.logo}
                  </div>
                  <div>
                    <p className="text-[15px] font-bold text-gray-900">{intg.name}</p>
                  </div>
                </div>
                <StatusBadge status={intg.status} />
              </div>

              <p className="text-[13px] text-gray-500 leading-relaxed mb-4">{intg.desc}</p>

              <ul className="space-y-1.5">
                {intg.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-[12px] text-gray-600">
                    <span className="text-green-500 text-[10px]">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* OJS highlight */}
      <div className="bg-blue-50 border-y border-blue-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="w-16 h-16 rounded-lg flex items-center justify-center text-white font-black text-xl flex-shrink-0" style={{ background: '#1a3a5c' }}>
              OJS
            </div>
            <div>
              <div className="flex items-center gap-3 mb-3">
                <h2 className="text-[22px] font-black text-gray-900">OJS integration in development</h2>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: '#dbeafe', color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Active development</span>
              </div>
              <p className="text-[14px] text-gray-600 leading-relaxed mb-4">
                We are currently building a native MannaPlag plugin for Open Journal Systems (OJS), the world's most widely used open source journal management platform. The plugin will allow journal editors to automatically screen manuscript submissions for plagiarism and AI-generated content directly within the OJS editorial workflow, without any manual file uploads or switching between systems.
              </p>
              <p className="text-[14px] text-gray-600 leading-relaxed mb-6">
                If your journal runs on OJS and you would like early access or want to participate in our beta testing programme, get in touch.
              </p>
              <Link
                to="/contact?inquiry=ojs"
                className="inline-block bg-navy text-white text-[13px] font-bold px-5 py-2.5 rounded-md hover:opacity-90 transition-opacity"
              >
                Register interest in OJS integration
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* API integration */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-[28px] font-black text-gray-900 mb-3">Integrate via REST API</h2>
          <p className="text-[14px] text-gray-500 max-w-xl mx-auto">
            While native LMS plugins are in development, our REST API allows you to integrate MannaPlag into any existing system today.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
          {apiFeatures.map(f => (
            <div key={f.title} className="bg-gray-50 border border-gray-100 rounded-xl p-6">
              <p className="text-[14px] font-bold text-gray-900 mb-2">{f.title}</p>
              <p className="text-[13px] text-gray-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="bg-navy rounded-lg p-6 overflow-x-auto">
          <p className="text-[11px] font-bold text-green-400 uppercase tracking-widest mb-3">Example API call</p>
          <pre className="text-[12px] text-green-400 font-mono leading-relaxed">{`POST /api/checker/analyze/
Authorization: Api-Key MAPL_your_key_here
Content-Type: multipart/form-data

file: manuscript.pdf
title: "Manuscript title"

→ 202 Accepted
{ "submission_id": "abc-123", "status": "processing" }

GET /api/submissions/abc-123/
→ 200 OK
{
  "status": "complete",
  "overall_similarity": 12,
  "overall_ai_score": 4,
  "report_id": "abc-123"
}`}</pre>
        </div>

        <div className="text-center mt-6">
          <Link to="/api-reference" className="text-[13px] font-bold text-blue-600 hover:underline">
            View full API documentation →
          </Link>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <h2 className="text-[22px] font-black text-gray-900 mb-3">Need a specific integration?</h2>
          <p className="text-[14px] text-gray-500 max-w-xl mx-auto mb-6">
            If you use a platform not listed here, contact us. We prioritise integration development based on demand from our users and partners.
          </p>
          <Link
            to="/contact?inquiry=integration"
            className="inline-block bg-navy text-white text-[13px] font-bold px-6 py-3 rounded-md hover:opacity-90 transition-opacity"
          >
            Request an integration
          </Link>
        </div>
      </div>

    </div>
  );
}
